"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import {
  Box,
  ChevronRight,
  Coins,
  Crown,
  House,
  Layers,
  LayoutDashboard,
  Megaphone,
  NotepadText,
  PanelLeft,
  RefreshCw,
  Settings,
  ShoppingBag,
  Tag,
  Ticket,
  TicketCheck,
  TrendingUp,
  UserSearch,
  Users,
  Workflow,
} from "lucide-react";
import { useInViewOnce } from "@/lib/hooks/use-in-view-once";
import { useMediaQuery } from "@/lib/hooks/use-media-query";
import { useRevealOnce } from "@/lib/hooks/use-reveal-once";
import { useTranslation } from "@/lib/hooks/use-translation";
import TierRowsMock from "@/components/home/mockups/tier-rows-mock";
import TierShareMock from "@/components/home/mockups/tier-share-mock";

type Card = {
  id: string;
  /** Where the text block sits in the card — the comp alternates the two. */
  copy: "top" | "bottom";
  /** The 496px cards in the comp: they take whatever the row has left. */
  wide?: boolean;
  /** Artwork for the media slot. Until one is set the card shows an empty
      slot of the right shape, so the layout is already final when the
      screenshots land (candidates are sitting in public/v2/feature). */
  image?: { src: string; alt: string };
  /** A hand-built panel instead of a flat image, for cards whose visual has
      to move. It fills the same slot, so the two are interchangeable. */
  media?: React.ComponentType;
};

/** Two rows of three, matching the comp: a wide card at one end of each. */
const ROWS: Card[][] = [
  [
    {
      id: "dashboard",
      wide: true,
      copy: "bottom",
      media: DashboardPeek,
    },
    {
      id: "ordering",
      copy: "top",
      media: OrderingPeek,
    },
    {
      id: "loyalty",
      copy: "top",
      media: LoyaltyPeek,
    },
  ],
  [
    {
      id: "anywhere",
      copy: "bottom",
      media: DevicesPeek,
    },
    {
      id: "stock",
      copy: "bottom",
      media: StockPeek,
    },
    {
      id: "marketing",
      wide: true,
      copy: "top",
      media: MarketingPeek,
    },
  ],
];

/** Flat order of the cards, which is what the entrance staggers along. */
const ORDER = ROWS.flat().map((card) => card.id);

/**
 * FeatureBento — v2 section 4: the six-card capability grid.
 *
 * Layout is the comp's bento: two rows of three, each row led (or closed) by a
 * wide card that absorbs the leftover width, so the row always adds up no
 * matter the container. Below `lg` the rows collapse into one column and the
 * fixed 320px card height is dropped — the copy sets the height there instead.
 *
 * Motion is the ReactBits masonry entrance, rebuilt in CSS: each card comes up
 * from below out of focus and resolves as it lands, staggered by `--bento-d`
 * (index across both rows, so the cascade reads as one sweep rather than two),
 * and hovering scales a card down a touch the way that component does. It
 * hangs off `useRevealOnce` like sections 2 and 3 — see globals.css,
 * "Section 4 · feature bento".
 *
 * The media slots are deliberately empty for now; drop a `image` into a card's
 * data and it renders in place of the placeholder, no layout work needed.
 */
export default function FeatureBento() {
  const { t } = useTranslation();
  const ref = useRevealOnce<HTMLElement>(0.35);

  return (
    <section
      ref={ref}
      id="feature-bento"
      aria-label={t("home.featureBento.aria")}
      // `scroll-mt-13` clears the fixed header for the navbar drawer's link here.
      className="bento-reveal relative scroll-mt-13 bg-[#FCFCFC]"
    >
      {/* Blue wash from the comp, sitting under the second row. Clipped by its
          own layer rather than by the section, because the cards fly in from
          below the fold — which is outside the section's box. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
      >
        <div className="absolute left-[18%] top-[50%] h-[100px] w-[60%] -rotate-[8deg] rounded-full bg-[rgba(0,134,240,0.25)] blur-[150px]" />
      </div>

      <div className="relative mx-auto flex w-full max-w-[1440px] flex-col items-center gap-8 px-4 py-12 md:px-[80px]">
        <header className="flex flex-col items-center gap-2 text-center">
          <h2 className="bento-title text-[26px]/[38px] font-semibold text-black [text-wrap:balance] sm:text-[32px]/[44px] lg:text-[40px]/[48px]">
            {t("home.featureBento.heading")}
          </h2>
          <p className="bento-sub text-[17px]/[26px] text-black/60 [text-wrap:balance] sm:text-[20px]/[28px]">
            {t("home.featureBento.sub")}
          </p>
        </header>

        <div className="flex w-full max-w-[1248px] flex-col gap-4">
          {ROWS.map((row, r) => (
            <div
              key={r}
              className="flex flex-col gap-4 lg:h-[320px] lg:flex-row"
            >
              {row.map((card) => (
                <BentoCard
                  key={card.id}
                  card={card}
                  // Counted across rows, so the stagger sweeps the whole grid.
                  index={ORDER.indexOf(card.id)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** The three sparks the badge throws on hover. Placed and drifted in `em`, so
    they scale with the badge's own type (14px on a phone, 16px above `sm`)
    rather than sitting at a fixed distance that reads differently at each size.
    The trio is deliberately uneven — same distance and timing three times reads
    as a machine, not as a spark. */
const BADGE_SPARKS = [
  {
    at: "left-[-0.15em] top-[-0.3em]",
    x: "-0.55em",
    y: "-0.6em",
    delay: "0s",
    dur: "0.9s",
  },
  {
    at: "right-[0.5em] top-[-0.35em]",
    x: "0.3em",
    y: "-0.75em",
    delay: "0.13s",
    dur: "1.05s",
  },
  {
    at: "right-[-0.2em] bottom-[-0.25em]",
    x: "0.6em",
    y: "0.5em",
    delay: "0.26s",
    dur: "0.95s",
  },
];

function Spark({
  at,
  x,
  y,
  delay,
  dur,
}: {
  /** Where it starts, as Tailwind position utilities. */
  at: string;
  /** How far it drifts before burning out. */
  x: string;
  y: string;
  delay: string;
  dur: string;
}) {
  return (
    <span
      style={
        {
          "--spark-x": x,
          "--spark-y": y,
          "--spark-d": delay,
          "--spark-dur": dur,
        } as React.CSSProperties
      }
      className={`bento-spark absolute block w-[0.62em] ${at}`}
    >
      <svg viewBox="0 0 24 24" fill="#3E9DEE" className="h-auto w-full">
        {/* Four-point star: straight out to each point, pinched back through
            the centre — the shape a glint makes, not a five-point star. */}
        <path d="M12 0c.7 6.6 4.7 10.6 12 12-7.3 1.4-11.3 5.4-12 12-.7-6.6-4.7-10.6-12-12 7.3-1.4 11.3-5.4 12-12z" />
      </svg>
    </span>
  );
}

function BentoCard({ card, index }: { card: Card; index: number }) {
  const { t } = useTranslation();
  // Below lg the six cards stack into a ~2361px column on a 375px phone, and
  // the section-wide cascade `--bento-d` drives is finished — all six cards,
  // 2.1s end to end — while the reader is still looking at the first one.
  // Stretching the stagger only fixes it for one scroll speed: too short and
  // the late cards still land unseen, too long and a quick scroller meets a
  // column of blanks. So each card carries its own observer and plays its
  // entrance on arrival, and the cascade is left to lg and up, where the rows
  // are on screen together and reading as one sweep is the whole point.
  //
  // The class lands at every width — the rules that consume it are inside a
  // max-width query, so above lg it is inert.
  const cardRef = useRevealOnce<HTMLElement>(0.25);

  const copy = (
    <div className="flex flex-col gap-2 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-[17px]/[24px] font-semibold text-black sm:text-[18px]/[24px]">
          {t(`home.featureBento.cards.${card.id}.title`)}
        </h3>
        {card.id === "marketing" && (
          <span
            tabIndex={0}
            className="bento-badge relative inline-block cursor-default rounded-full bg-[linear-gradient(90deg,#D9EBFF_0%,#F2F7FF_20%,#C7E0FA_35%,#FFFFFF_50%,#CCE5FF_65%,#EBF5FF_85%,#BFDEF7_100%)] px-2 text-[14px]/[24px] font-medium text-[#002E52] shadow-[inset_0_1px_2px_rgba(255,255,255,0.7),0_1px_3px_rgba(102,153,229,0.25)] outline outline-1 -outline-offset-1 outline-[#B2D9FF] sm:text-[16px]/[24px]"
          >
            {/* The sheen has to be clipped to the pill, the sparks have to
                escape it — hence two layers rather than one ::after. */}
            <span
              aria-hidden
              className="bento-badge-sheen pointer-events-none absolute inset-0 overflow-hidden rounded-full"
            />
            <span className="relative">
              {t("home.featureBento.cards.marketing.badge")}
            </span>
            <span aria-hidden className="pointer-events-none absolute inset-0">
              {BADGE_SPARKS.map((spark, index) => (
                <Spark key={index} {...spark} />
              ))}
            </span>
          </span>
        )}
      </div>
      <p className="text-[13px]/[20px] text-black/60 sm:text-[14px]/[20px]">
        {t(`home.featureBento.cards.${card.id}.body`)}
      </p>
    </div>
  );

  return (
    <article
      ref={cardRef}
      // Drives this card's slot in the cascade; see globals.css.
      style={{ "--bento-d": `${index * 0.18}s` } as React.CSSProperties}
      className={`bento-card flex flex-col overflow-hidden rounded-[24px] bg-white outline outline-1 -outline-offset-1 outline-[#EFEFEF] shadow-[0_10px_30px_-20px_rgba(15,23,42,0.25)] transition-transform duration-300 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] hover:scale-[0.95] ${
        // Proportional rather than fixed: 1.378 : 1 : 1 lands on the comp's
        // 496 / 360 / 360 at full width, and keeps that balance as the row
        // narrows instead of squeezing the wide card on its own.
        card.wide
          ? "lg:min-w-0 lg:basis-0 lg:grow-[1.378]"
          : "lg:min-w-0 lg:basis-0 lg:grow"
      }`}
    >
      {card.copy === "top" && copy}

      <div
        className={`relative aspect-[16/10] flex-1 lg:aspect-auto lg:min-h-[120px] ${
          card.copy === "top" ? "mx-4 mb-4" : "mx-4 mt-4"
        } ${
          // Images fill the slot and are cropped to it; hand-built media place
          // themselves and are cropped by the card, which is how the phone
          // below runs off the bottom edge.
          card.media ? "" : "overflow-hidden rounded-[18px]"
        }`}
      >
        {/* Stays blank until the card gets a `media` or an `image` — the slot
            keeps its size either way, so nothing shifts when one lands. */}
        {card.media ? (
          <card.media />
        ) : (
          card.image && (
            <Image
              src={card.image.src}
              alt={card.image.alt}
              fill
              sizes="(max-width: 1024px) 100vw, 496px"
              className="object-cover"
            />
          )
        )}
      </div>

      {card.copy === "bottom" && copy}
    </article>
  );
}

/**
 * The "รู้ทันธุรกิจ" card's visual: the live tier panel with a screenshot of
 * the table view overlapping it, both cropped by the card the way the comp
 * frames them.
 *
 * Placement is in percentages of the slot rather than the comp's pixels, so
 * the pair keeps its composition as the card's width changes. The two pieces
 * rise a short way from below as they arrive — the card's own entrance,
 * scaled down — and the panel starts counting at the same moment.
 */
function DashboardPeek() {
  const { t } = useTranslation();
  const { ref, inView } = useInViewOnce<HTMLDivElement>(0.4);

  return (
    <div ref={ref} className="absolute inset-0">
      <div className="bento-media absolute left-[2%] top-0 w-[50%]">
        {/* Matches the media slot's own entrance delay in globals.css, so the
            figures start from zero on the frame the panel becomes visible. */}
        <TierShareMock armed={inView} lead={620} />
      </div>

      <div
        // A beat behind the panel, so the screenshot reads as landing on top.
        style={{ "--media-d": "0.12s" } as React.CSSProperties}
        className="bento-media absolute left-[42.7%] top-[21%] w-[57%]"
      >
        <Image
          src="/v2/feature/Frame 478.webp"
          alt={t("home.featureBento.alt.tables")}
          width={1060}
          height={652}
          sizes="(max-width: 1024px) 60vw, 280px"
          className="h-auto w-full rounded-[0.25em] shadow-[-0.19em_0.19em_0.85em_rgba(0,0,0,0.08)]"
        />
      </div>
    </div>
  );
}

/**
 * The "ออเดอร์ไว" card's visual: the customer-facing phone with the three
 * people it serves — host, chef and waiter — orbiting it.
 *
 * Laid out in the comp's own pixels, converted once: the stage's font-size is
 * 1% of its width, so `1em` is a percentage point and every offset below is
 * the comp figure over the slot's 328px. That keeps the whole composition
 * locked together as the card resizes, instead of four pieces each drifting
 * on their own percentage.
 *
 * The bubble artwork is a 227px circle sitting at (47, 8) in a 320px canvas —
 * the rest is its drop shadow — so a box of 1.41x the circle, nudged back by
 * the inset, puts the visible circle exactly where the comp asks for it.
 */
function OrderingPeek() {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0" style={{ containerType: "inline-size" }}>
      <div className="relative h-full text-[1cqw]">
        <Bubble
          src="/v2/feature/div.absolute.webp"
          alt={t("home.featureBento.alt.host")}
          className="left-[6.5em] top-[-2.7em]"
          float="4.3s"
          // Held back: the card's copy sits directly above this one.
          rise="-3.4em"
        />
        <Bubble
          src="/v2/feature/div.absolute2.webp"
          alt={t("home.featureBento.alt.chef")}
          className="left-[10.2em] top-[29em]"
          delay="0.18s"
          float="5.4s"
          rise="-6em"
        />

        {/* Above the two on the left, below the one on the right — the comp's
            stacking, which reads as the phone sitting among them. */}
        <div className="bento-slide-up absolute left-[22.3em] top-0 w-[55.2em]">
          <Image
            src="/v2/hero-phone.webp"
            alt={t("home.featureBento.alt.phoneOrder")}
            width={646}
            height={1308}
            sizes="(max-width: 1024px) 55vw, 200px"
            className="h-auto w-full"
          />
        </div>

        <Bubble
          src="/v2/feature/div.absolute3.webp"
          alt={t("home.featureBento.alt.server")}
          className="left-[70.9em] top-[14.3em]"
          delay="0.26s"
          float="4.8s"
          rise="-5.5em"
        />
      </div>
    </div>
  );
}

function Bubble({
  src,
  alt,
  className,
  delay = "0.1s",
  float: floatDuration,
  rise,
}: {
  src: string;
  alt: string;
  className: string;
  delay?: string;
  /** Period of the idle bob. Distinct per bubble — see globals.css. */
  float: string;
  /** How far it drifts up, in stage em. */
  rise: string;
}) {
  return (
    <div
      style={
        {
          "--media-d": delay,
          "--float-dur": floatDuration,
          "--float-y": rise,
        } as React.CSSProperties
      }
      className={`bento-media absolute w-[24em] ${className}`}
    >
      {/* Separate element for the bob: the entrance owns the transform on the
          wrapper, this owns the one that runs forever. */}
      <div className="bento-float">
        <Image
          src={src}
          alt={alt}
          width={320}
          height={320}
          sizes="(max-width: 1024px) 24vw, 80px"
          className="h-auto w-full"
        />
      </div>
    </div>
  );
}

/**
 * The "ลูกค้ากลับมาใช้บริการซ้ำ" card's visual: the tier table, laid in at the
 * comp's own size and left to run off the card's right edge.
 *
 * Same em-per-percentage-point stage as the card above, so the comp's 24px
 * left inset and 747.83px row width convert straight across and the table
 * keeps its crop as the card resizes.
 */
function LoyaltyPeek() {
  return (
    <div className="absolute inset-0" style={{ containerType: "inline-size" }}>
      <div className="relative h-full text-[1cqw]">
        <div className="absolute left-[2.4em] top-0 w-[228em]">
          <TierRowsMock />
        </div>
      </div>
    </div>
  );
}

/**
 * The "บริหารร้านได้ ไม่ว่าคุณจะอยู่ที่ไหน" card's visual: the same store seen
 * on all three devices at once — laptop behind, phone and tablet standing in
 * front of it and overlapping its corners, as in the comp.
 *
 * Same stage as the cards above: the container's font-size is 1% of its width,
 * so every offset here is a percentage point and the three devices keep their
 * arrangement — including the overlaps — at any card width. Each one is sized
 * by width alone; `h-auto` lets its own aspect ratio set the height, so the
 * devices never distort.
 *
 * They arrive back-to-front, a beat apart, which reads as the laptop landing
 * and the two smaller devices being set down in front of it.
 */
function DevicesPeek() {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0" style={{ containerType: "inline-size" }}>
      <div className="relative h-full text-[1cqw]">
        <div className="bento-media absolute left-[13.2em] top-[1.6em] w-[80.7em]">
          <Image
            src="/v2/feature/MacBookPOSDash.webp"
            alt={t("home.featureBento.alt.desktop")}
            width={1060}
            height={641}
            sizes="(max-width: 1024px) 80vw, 300px"
            className="h-auto w-full"
          />
        </div>

        {/* In front of the laptop, overlapping its right side. */}
        <div
          style={{ "--media-d": "0.1s" } as React.CSSProperties}
          className="bento-media absolute left-[65.2em] top-[10.7em] w-[31.3em]"
        >
          <Image
            src="/v2/feature/IPadPOS.webp"
            alt={t("home.featureBento.alt.tablet")}
            width={422}
            height={584}
            sizes="(max-width: 1024px) 32vw, 110px"
            className="h-auto w-full"
          />
        </div>

        {/* Nearest the viewer, over the laptop's left corner. */}
        <div
          style={{ "--media-d": "0.18s" } as React.CSSProperties}
          className="bento-media absolute left-[7.5em] top-[13.2em] w-[19.8em]"
        >
          <Image
            src="/v2/feature/iPhonePOS.webp"
            alt={t("home.featureBento.alt.mobile")}
            width={259}
            height={524}
            sizes="(max-width: 1024px) 20vw, 70px"
            className="h-auto w-full"
          />
        </div>
      </div>
    </div>
  );
}

/** The three stock figures, top to bottom as in the comp. Each one waits for
    the branch that ends at it, then slides in from the right behind it — see
    `bento-from-right` in globals.css. The middle row is a straight run from
    the tip and lands first; the outer two arrive together once their branches
    have climbed the trunk and turned out to the dots. */
const STOCK_ROWS = [
  { src: "/v2/feature/stockIn.webp", altKey: "stockIn", delay: "0.73s" },
  { src: "/v2/feature/stockOut.webp", altKey: "stockOut", delay: "0.45s" },
  { src: "/v2/feature/changeStock.webp", altKey: "stockNet", delay: "0.73s" },
];

/** The systems the stock numbers flow out to. All three share one ink; only
    Jelloducks, as the hub the connector points at, is set a size larger. */
const STOCK_PILLS = [
  { label: "POS", hub: false, delay: "0.05s" },
  { label: "Jelloducks", hub: true, delay: "0s" },
  { label: "CRM", hub: false, delay: "0.1s" },
];

/**
 * The "ไม่พลาดการขาย" card's visual: the three stock figures on the left, the
 * connector fanning out of them, and the systems they feed on the right.
 *
 * Same stage as the cards above — the container's font-size is 1% of its width,
 * so every number here is a percentage point and the three parts stay locked
 * together as the card resizes. That matters more here than on the other cards
 * because the connector has to *touch* both ends: its dots sit on the row
 * centres and its right tip meets the Jelloducks pill, and both alignments are
 * derived from the same figures rather than eyeballed.
 *
 * The rows are a flex column rather than three absolute tops: the three
 * screenshots differ by a pixel or two in height, and a fixed pitch would show
 * that as uneven gaps.
 *
 * Motion runs right to left, following the connector: the pills arrive, the
 * three branches draw out of them, and each stock figure appears as the branch
 * that ends at it lands. The delays below and the ones on `STOCK_ROWS` are two
 * halves of one timeline — a branch's start plus its duration is the delay of
 * the row it feeds — so they have to be read together when retiming it.
 */
function StockPeek() {
  const { t } = useTranslation();
  return (
    <div className="absolute inset-0" style={{ containerType: "inline-size" }}>
      <div className="relative h-full text-[1cqw]">
        <div className="absolute left-[3.7em] top-[0.4em] flex w-[58.2em] flex-col gap-[3.67em]">
          {STOCK_ROWS.map((row) => (
            <div
              key={row.src}
              style={{ "--media-d": row.delay } as React.CSSProperties}
              className="bento-from-right"
            >
              {/* unoptimized: these are 795px-wide UI screenshots rendered
                  into a ~191px slot, and the optimizer's 384w/q75 variant
                  crushes 4.4 KB of crisp small type down to 1.5 KB — the text
                  goes to mush for ~3 KB saved. Serving the original hands the
                  browser 4x the pixels of the slot and one clean downscale,
                  so it stays sharp at any DPR. No `sizes`: with no srcset to
                  choose from it would do nothing. */}
              <Image
                src={row.src}
                alt={t(`home.featureBento.alt.${row.altKey}`)}
                width={795}
                height={186}
                unoptimized
                className="h-auto w-full"
              />
            </div>
          ))}
        </div>

        {/* Sized off the rows, not the comp: the artwork's outer dots sit at
            2.5 and 119.8 of its 123 tall box, so scaling that span onto the
            distance between the first and last row centre — and backing the
            box off by the same 2.5 — lands the dots on the rows exactly. */}
        <div className="absolute left-[61.16em] top-[6.46em] w-[26.55em]">
          <ConnectorLines className="h-auto w-full" />
        </div>

        {/* Right-aligned, so the pills line up on their right edge and only
            grow leftward with the length of the label, as in the comp. */}
        <div className="absolute right-[5.3em] top-[12.3em] flex flex-col items-end gap-[4.4em]">
          {STOCK_PILLS.map((pill) => (
            <div
              key={pill.label}
              style={{ "--media-d": pill.delay } as React.CSSProperties}
              className="bento-media rounded-full bg-[linear-gradient(135deg,#FFFFFF_0%,#EDF3FD_100%)] px-[2.87em] py-[0.32em] shadow-[0_0.478em_0.716em_-0.478em_rgba(0,0,0,0.10),0_1.194em_1.791em_rgba(0,0,0,0.05)] outline outline-[0.238em] -outline-offset-[0.238em] outline-white"
            >
              <span
                className={`block whitespace-nowrap text-center font-medium text-[#1D2330] ${
                  pill.hub ? "text-[3.35em]/[1.333]" : "text-[3.05em]/[1.464]"
                }`}
              >
                {pill.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The connector between the stock rows and the Jelloducks pill, traced from
 * the comp's export (`public/v2/feature/Line.svg`). Inlined rather than loaded
 * as an image because the paths animate individually — an <Image> is one
 * opaque box — and it needs no `dangerouslyAllowSVG` in next.config either.
 *
 * The order of the runs in `CONNECTOR_RUNS` is the order they are drawn in,
 * not the export's; see the notes there, and `bento-draw` and `bento-flow` in
 * globals.css.
 *
 * The drawn content stops at x≈50 of the 90-wide box; the remaining width is
 * the artwork's own trailing space, and `StockPeek` accounts for it when it
 * places the pill the lines point at.
 */
/**
 * One run of the connector. `draw` is its entrance (see `bento-draw`), `flow`
 * its place in the pulse that loops afterwards (see `bento-flow`).
 *
 * The two groups below render from this one list rather than repeating the
 * path data, so the pulse can never drift off the line it is supposed to be
 * running along.
 *
 * `from` / `reverse` are the same fact twice: a path is revealed, and a dash
 * travelled, from whichever end the browser calls its start, so the two
 * branches whose `d` runs left-to-right have to be driven backwards to move
 * with everything else.
 */
type Run = {
  d: string;
  draw: { delay: string; dur: string; from?: number };
  flow: { delay: string; reverse?: boolean };
};

const CONNECTOR_RUNS: Run[] = [
  // First wave, all three leaving the tip at once: up the trunk, down the
  // trunk, and straight across to the middle dot. The straight run is the
  // shortest, so it is given the shortest duration and gets there while the
  // other two are still climbing.
  {
    d: "M48.1219 60.5078L42.3056 60.5078C35.9081 60.5078 30.7219 55.3217 30.7219 48.9242L30.7219 24.8861",
    draw: { delay: "0.15s", dur: "0.3s" },
    flow: { delay: "0s" },
  },
  {
    d: "M49.8619 61.3091L42.3056 61.3091C35.9081 61.3091 30.7219 66.4952 30.7219 72.8927L30.7219 97.9383",
    draw: { delay: "0.15s", dur: "0.3s" },
    flow: { delay: "0s" },
  },
  {
    d: "M46 61L3 61",
    draw: { delay: "0.15s", dur: "0.28s" },
    flow: { delay: "0s" },
  },

  // Second wave, picking up mid-trunk where the first left off and turning out
  // to the two outer dots.
  //
  // The pulse's delay is not the draw's. A branch is *drawn* once the run
  // feeding it has finished, but a travelling dash has to be handed over where
  // the two actually meet: these branches end at y≈38.5 and y≈81.5, which the
  // trunk runs above and below on its way to y≈24.9 and y≈97.9. The first
  // wave's dash passes those points about 70% of the way through its own
  // travel, which is where 0.63s comes from — start it at the draw's 0.45s and
  // the pulse visibly jumps back down the trunk before setting off again.
  {
    d: "M1.14192 2.91576L19.1383 2.91577C25.5358 2.91577 30.7219 8.10193 30.7219 14.4994L30.7219 38.5375",
    draw: { delay: "0.45s", dur: "0.28s", from: -1 },
    flow: { delay: "0.63s", reverse: true },
  },
  {
    d: "M1.14192 119.916L19.1383 119.916C25.5358 119.916 30.7219 114.73 30.7219 108.332L30.7219 81.4551",
    draw: { delay: "0.45s", dur: "0.28s", from: -1 },
    flow: { delay: "0.63s", reverse: true },
  },
];

function ConnectorLines({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 90 123"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={className}
    >
      <g
        stroke="#D9EDFC"
        strokeWidth="1.46517"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {CONNECTOR_RUNS.map((run) => (
          <path
            key={run.d}
            className="bento-draw"
            style={
              {
                "--draw-d": run.draw.delay,
                "--draw-dur": run.draw.dur,
                ...(run.draw.from ? { "--draw-from": run.draw.from } : {}),
              } as React.CSSProperties
            }
            pathLength="1"
            d={run.d}
          />
        ))}
      </g>

      {/* The same five runs again in the accent blue, each carrying a short
          dash that travels its length on a loop once the connector has drawn
          itself — the card's "this is live" tell, and the one thing here that
          says which way the stock numbers are moving. Painted between the
          lines and the dots, so a pulse runs over the line it belongs to and
          under the dot it is arriving at. */}
      <g
        stroke="#0084EB"
        strokeWidth="1.46517"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {CONNECTOR_RUNS.map((run) => (
          <path
            key={run.d}
            className="bento-flow"
            style={
              {
                "--flow-d": run.flow.delay,
                ...(run.flow.reverse
                  ? { "--flow-from": -0.84, "--flow-to": 0 }
                  : {}),
              } as React.CSSProperties
            }
            pathLength="1"
            d={run.d}
          />
        ))}
      </g>

      <g stroke="#D9EDFC" strokeLinecap="round" strokeLinejoin="round">
        {/* Each dot pops as its branch arrives. */}
        <path
          className="bento-dot"
          style={{ "--dot-d": "0.43s" } as React.CSSProperties}
          d="M2.57568 61.8628C1.86458 61.8628 1.28776 61.2867 1.2876 60.5757C1.2876 59.8645 1.86448 59.2876 2.57568 59.2876C3.28675 59.2878 3.86279 59.8646 3.86279 60.5757C3.86263 61.2866 3.28665 61.8626 2.57568 61.8628Z"
          strokeWidth="2.5755"
        />
        <path
          className="bento-dot"
          style={{ "--dot-d": "0.73s" } as React.CSSProperties}
          d="M2.5 3.71191C1.83049 3.71191 1.28809 3.16951 1.28809 2.5C1.28809 1.83049 1.83049 1.28809 2.5 1.28809C3.16951 1.28809 3.71191 1.83049 3.71191 2.5C3.71191 3.16951 3.16951 3.71191 2.5 3.71191Z"
          strokeWidth="2.5755"
        />
        <path
          className="bento-dot"
          style={{ "--dot-d": "0.73s" } as React.CSSProperties}
          d="M2.81299 121.219C2.03634 121.219 1.40674 120.589 1.40674 119.812C1.40674 119.036 2.03634 118.406 2.81299 118.406C3.58964 118.406 4.21924 119.036 4.21924 119.812C4.21923 120.589 3.58963 121.219 2.81299 121.219Z"
          strokeWidth="2.81251"
        />
      </g>
    </svg>
  );
}

/**
 * The "ทำการตลาด" card's visual: the CRM's own sidebar on the left, with the
 * coupon builder it opens sitting beside it and running off the card's right
 * edge — the comp's framing.
 *
 * The sidebar is rebuilt rather than screenshotted because it has to move: a
 * pointer walks down the menu, presses a row, and the row's sub-items unfold
 * under it, then fold away again and the pointer moves on — looping for as
 * long as the card is on screen. A flat image can't do that, and rebuilding it
 * also keeps the type crisp at this size.
 *
 * Everything is measured in the real sidebar's own pixels and converted once
 * through `u()`: the stage's font-size is 1% of the slot's width, so `1em` is
 * a percentage point, and `u(32)` is "32px of a 208px sidebar" expressed as a
 * share of the card. The whole panel therefore keeps its proportions at any
 * card width, and the row pitch the pointer steps along is the same number the
 * rows are laid out with instead of a second, hand-tuned one.
 */

/** The sidebar's width as a share of the media slot, and px → stage em from
    it. 208px is the real sidebar's width, so every figure below is the
    product's own. */
const NAV_W = 32;
const u = (px: number) => `${((px * NAV_W) / 208).toFixed(3)}em`;

/** Row height (h-8) plus the list's gap-1 — the pitch of the menu. */
const NAV_PITCH = 36;

const NAV_ITEMS = [
  {
    labelKey: "dashboard",
    color: "#2CA6E2",
    Icon: House,
    /** The product's own dashboard group, Broadcast included — which is the
        card's copy ("โปรโมชัน คูปอง และ Broadcast") pointed at on screen. */
    children: [
      { labelKey: "overview", Icon: LayoutDashboard },
      { labelKey: "revenue", Icon: TrendingUp },
      { labelKey: "members", Icon: Users },
      { labelKey: "points", Icon: Coins },
      { labelKey: "coupons", Icon: Ticket },
      { labelKey: "broadcast", Icon: Megaphone },
    ],
  },
  {
    labelKey: "customers",
    color: "#43B75D",
    Icon: Users,
    children: [
      { labelKey: "customerList", Icon: Users },
      { labelKey: "customerSearch", Icon: UserSearch },
    ],
  },
  {
    labelKey: "sales",
    color: "#F89204",
    Icon: ShoppingBag,
    children: [
      { labelKey: "products", Icon: Box },
      // The product draws this one itself; NotepadText is the same shape.
      { labelKey: "orders", Icon: NotepadText },
    ],
  },
  {
    labelKey: "tiers",
    color: "#FFBB33",
    Icon: Crown,
    children: [
      { labelKey: "tiers", Icon: Users },
      { labelKey: "tierAdjust", Icon: RefreshCw },
    ],
  },
  {
    labelKey: "points",
    color: "#A96BF1",
    Icon: Coins,
    children: [
      { labelKey: "pointSettings", Icon: Settings },
      { labelKey: "pointAdjust", Icon: Coins },
    ],
  },
  {
    labelKey: "coupons",
    color: "#FF69F8",
    Icon: Ticket,
    children: [
      { labelKey: "couponManage", Icon: Tag },
      { labelKey: "couponGroups", Icon: Layers },
      { labelKey: "couponIndividual", Icon: TicketCheck },
      { labelKey: "couponAuto", Icon: Workflow },
      { labelKey: "broadcastCampaign", Icon: Megaphone },
    ],
    /** The page the screenshot beside it is showing, so it stays selected. */
    current: true,
  },
];

/** The rows the demo opens, in order. Only the upper four: a row further down
    would unfold below the card's bottom edge, so the press would land on a
    submenu nobody can see. คูปอง stays selected throughout instead — it is the
    page on the right. */
const NAV_DEMO = [0, 1, 2, 3];

/** One beat of the loop: the pointer travels to the next row, presses it, the
    group stays open, then closes with a breath before the next row. The first
    beat waits for the card's own entrance to finish before anything moves. */
const NAV_TRAVEL = 620;
const NAV_LEAD = 1700;
const NAV_HOLD = 1700;
const NAV_GAP = 480;
const NAV_BEAT = NAV_TRAVEL + NAV_HOLD + NAV_GAP;

function MarketingPeek() {
  const { t } = useTranslation();
  const { ref, inView } = useInViewOnce<HTMLDivElement>(0.4);
  const still = useMediaQuery("(prefers-reduced-motion: reduce)");
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(false);
  /** Bumped on every press purely to re-key the tap ring, which replays its
      animation by being a new element rather than by resetting a class. */
  const [tap, setTap] = useState(0);

  const i = NAV_DEMO[step % NAV_DEMO.length];
  const playing = inView && !still;

  // One interval owns the whole loop, and `step` is a plain counter it writes
  // to. The obvious alternative — chaining the next beat off a timer and
  // depending on `step` — re-arms the chain on every state change, and a beat
  // that lands while React is already re-rendering can advance the counter
  // twice, which shows up as the pointer skipping a row.
  useEffect(() => {
    if (!playing) return;

    let beat = 0;
    let cycle: ReturnType<typeof setInterval> | undefined;
    let inner: ReturnType<typeof setTimeout>[] = [];

    const play = () => {
      // Only this beat's own timers are ever outstanding.
      inner.forEach(clearTimeout);
      setStep(beat++);
      inner = [
        setTimeout(() => {
          setOpen(true);
          setTap((n) => n + 1);
        }, NAV_TRAVEL),
        setTimeout(() => setOpen(false), NAV_TRAVEL + NAV_HOLD),
      ];
    };

    const lead = setTimeout(() => {
      play();
      cycle = setInterval(play, NAV_BEAT);
    }, NAV_LEAD);

    return () => {
      clearTimeout(lead);
      clearInterval(cycle);
      inner.forEach(clearTimeout);
    };
  }, [playing]);

  return (
    <div
      ref={ref}
      className="absolute inset-0"
      style={{ containerType: "inline-size" }}
    >
      <div className="relative h-full text-[1cqw]">
        {/* The sidebar. `items-start` on the column is what lets a group grow
            downward without stretching the rows above it. */}
        <div
          className="bento-media absolute left-0 top-0 flex flex-col rounded-[1.9em] bg-white shadow-[0_0.6em_1.8em_-0.9em_rgba(15,23,42,0.28),0_0.15em_0.5em_-0.3em_rgba(15,23,42,0.12)]"
          style={{ width: `${NAV_W}em`, padding: u(8), gap: u(4) }}
        >
          {/* The panel's own header row, as in the product. */}
          <div
            className="flex shrink-0 items-center rounded-[0.6em]"
            style={{ height: u(32), gap: u(8), paddingInline: u(8) }}
          >
            <PanelLeft
              style={{ width: u(16), height: u(16) }}
              className="text-[#1D2330]"
            />
            <span
              className="font-semibold text-[#2CA6E2]"
              style={{ fontSize: u(16), lineHeight: 1.45 }}
            >
              Jelloducks CRM
            </span>
          </div>

          {NAV_ITEMS.map((item, index) => {
            // The pointer is resting on this row, and it is open once pressed.
            const focused = playing && index === i;
            const expanded = focused && open;

            return (
              <div
                key={item.labelKey}
                className="flex shrink-0 flex-col"
                style={{ gap: u(2) }}
              >
                <div
                  className={`flex items-center rounded-[0.6em] transition-[background-color,transform] duration-200 ${
                    // The press itself: a shallow squash under the pointer.
                    expanded ? "scale-[0.985]" : ""
                  } ${
                    focused
                      ? "bg-[#F1F5F9]"
                      : item.current
                        ? "bg-[#F4F0FF]"
                        : "bg-transparent"
                  }`}
                  style={{ height: u(32), gap: u(8), paddingInline: u(8) }}
                >
                  <item.Icon
                    style={{ width: u(16), height: u(16), color: item.color }}
                    className="shrink-0"
                  />
                  <span
                    className={`truncate text-[#1D2330] ${item.current ? "font-medium" : ""}`}
                    style={{ fontSize: u(14), lineHeight: 1.5 }}
                  >
                    {t(`home.featureBento.crmNav.${item.labelKey}`)}
                  </span>
                  <ChevronRight
                    style={{ width: u(16), height: u(16) }}
                    className={`ml-auto shrink-0 text-[#8A8A8A] transition-transform duration-300 ${
                      expanded ? "rotate-90" : ""
                    }`}
                  />
                </div>

                {/* 0fr → 1fr rather than a height: the sub-items set their own
                    size, so the group opens to exactly what it holds without
                    a measured max-height to keep in sync. */}
                <div
                  className="grid transition-[grid-template-rows,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]"
                  style={{
                    gridTemplateRows: expanded ? "1fr" : "0fr",
                    opacity: expanded ? 1 : 0,
                  }}
                >
                  <div className="overflow-hidden">
                    {/* The product's sub-menu list: indented off a hairline
                        rule, each row an icon and a label at the same size as
                        the group above it. */}
                    <div
                      className="flex flex-col border-l border-[#E4E4E7]"
                      style={{
                        marginLeft: u(14),
                        paddingLeft: u(10),
                        paddingBlock: u(4),
                        gap: u(4),
                      }}
                    >
                      {item.children.map((child) => (
                        <span
                          key={child.labelKey}
                          // No selected state inside a group: คูปอง is the page
                          // being shown, so every other group opens with all of
                          // its rows at rest, exactly as the product does.
                          className="flex items-center rounded-[0.6em] text-[#3F3F46]"
                          // The font-size goes on the label, not on the row:
                          // `u()` is in stage em, and setting it here would
                          // make the row its own em context — the icon beside
                          // it would then be sized off 14px type rather than
                          // off the card, and come out twice too big.
                          style={{
                            height: u(28),
                            gap: u(8),
                            paddingInline: u(8),
                          }}
                        >
                          <child.Icon
                            style={{ width: u(16), height: u(16) }}
                            className="shrink-0 text-[#71717A]"
                          />
                          <span
                            className="truncate"
                            style={{ fontSize: u(14), lineHeight: 1.5 }}
                          >
                            {t(`home.featureBento.crmNav.${child.labelKey}`)}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* The pointer. It sits over the row it is about to press — the y is
            the same pitch the rows are laid out with, so the two can't drift
            apart — and the ring under it flashes on the press. */}
        {playing && (
          <div
            aria-hidden
            className="pointer-events-none absolute left-0 top-0 transition-transform duration-[520ms] ease-[cubic-bezier(0.33,1,0.68,1)]"
            style={{
              transform: `translate3d(${u(116)}, ${u(8 + (i + 1) * NAV_PITCH + 19)}, 0)`,
            }}
          >
            <span
              key={tap}
              className="bento-tap absolute rounded-full bg-[#2CA6E2]"
              style={{ width: u(26), height: u(26), left: u(-11), top: u(-11) }}
            />
            <Pointer
              style={{ width: u(17), height: u(17) }}
              className="relative block"
            />
          </div>
        )}

        {/* The coupon builder the sidebar opens onto, cropped by the card on
            its right and bottom edges as in the comp. */}
        <div
          style={{ "--media-d": "0.12s" } as React.CSSProperties}
          className="bento-media absolute left-[34em] top-[-2em] w-[72em]"
        >
          <Image
            src="/v2/feature/Coupon Main Hub.webp"
            alt={t("home.featureBento.alt.coupon")}
            width={1043}
            height={901}
            sizes="(max-width: 1024px) 72vw, 340px"
            className="h-auto w-full"
          />
        </div>
      </div>
    </div>
  );
}

/** The demo pointer. Drawn here rather than taken from lucide because it needs
    a white rim to stay readable over both the panel and the screenshot. */
function Pointer({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <svg viewBox="0 0 24 24" className={className} style={style} aria-hidden>
      <path
        d="M5.5 3.2 19 11.3l-5.9 1.5-2.4 5.7z"
        fill="#1D2330"
        stroke="#FFFFFF"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  );
}
